// Create run -> upload to S3 -> start BDA (requirements 5.1-5.4).
import type { VersionRef } from '@bda/shared';
import { api, ApiError } from '../../api/client';
import { contentTypeOf, uploadToS3 } from '../../lib/upload';

export interface StartDocumentRun {
  file: File;
  /** `set`: a Set_Run (`setId`, `setVersion`); `packetSuggest`: a Packet_Suggest_Run (`prompt`) (blueprint-sets). */
  mode: 'custom' | 'suggest' | 'set' | 'packetSuggest';
  blueprintId?: string;
  version?: VersionRef;
  /** Set runs: the set and its version ('DRAFT' or a published number). */
  setId?: string;
  setVersion?: VersionRef;
  /** Suggest and packet-suggest runs: the optional "What do you want to extract?" text (requirement 3.7.2). */
  prompt?: string;
  onProgress?: (fraction: number) => void;
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** The create-run fields of one mode; fields of other modes are left out so the API's checks stay simple. */
function modeFields(input: StartDocumentRun) {
  switch (input.mode) {
    case 'custom':
      return { blueprintId: input.blueprintId, version: input.version };
    case 'set':
      return { setId: input.setId, setVersion: input.setVersion };
    case 'suggest':
    case 'packetSuggest':
      return input.prompt ? { prompt: input.prompt } : {};
  }
}

export async function startDocumentRun(input: StartDocumentRun): Promise<string> {
  const contentType = contentTypeOf(input.file);
  if (!contentType) throw new Error('Upload a PDF, PNG, JPEG, or TIFF file.');
  const { run, upload } = await api.createRun({
    mode: input.mode,
    ...modeFields(input),
    fileName: input.file.name,
    contentType,
    size: input.file.size,
  });
  try {
    await uploadToS3(upload, input.file, input.onProgress);
  } catch {
    // The pre-signed form may have expired or the network dropped: get a fresh form and try once more.
    const renewed = await api.renewUpload(run.id);
    await uploadToS3(renewed.upload, input.file, input.onProgress);
  }
  // BDA throttling surfaces as 429; back off and retry the start a few times (design 7).
  for (let attempt = 0; ; attempt++) {
    try {
      await api.startRun(run.id);
      return run.id;
    } catch (e) {
      if (e instanceof ApiError && e.code === 'THROTTLED' && attempt < 4) {
        await sleep(1000 * 2 ** attempt);
        continue;
      }
      throw e;
    }
  }
}
